import {
  ChildProgramComponentInput,
  CloneProgramMutation,
  ComponentStatus,
  CreateProgramMutation,
  GetProgramCoachingSessionParticipantsQuery,
  ProgramComponentForStatisticQuery,
  ProgramComponentInput,
  ProgramComponentParticipantsQuery,
  ProgramComponentQuery,
  ProgramComponentResponseQuery,
  ProgramComponentsQuery,
  ProgramQuery,
  ProgramStatus,
  ProgramsQuery,
  ProgramUsersQuery,
  SurveyTemplatesQuery,
} from "@/graphql";
import { NonNullableArrayItem, Resolve } from "@/utils/types";

import { ProgramScenario } from "./roleplays";
import { ProgramFormField } from "./surveys";

// ================================
// String Unions Types
// ================================

export type ProgramRole = "lead" | "participant";

export type ComponentDialog =
  | "add-component"
  | "edit-component"
  | "delete-component"
  | "close-component"
  | "add-context"
  | "grant-extension";

export type ProgramDialog =
  | "add-programs"
  | "update-program"
  | "archive-program"
  | "end-program"
  | "clone-program";

export type MeetingPlatforms =
  | "Zoom"
  | "GoogleMeet"
  | "MicrosoftTeams"
  | "Others";

export type CoachingStage =
  | "NotStarted"
  | "IntrosExpired"
  | "InIntros"
  | "CoachPicked"
  | "SessionActive"
  | "Completed"
  | "Scheduled"
  | "Missed"
  | "InCompleted";

export type ProgramTableField =
  | "name"
  | "status"
  | "participant_count"
  | "target_launch_date"
  | "actions"
  | "total_participant_ongoing_surveys";

export type ParticipantTableField =
  | "stage"
  | "coach"
  | "selectedCoach"
  | "totalCreditsUsed"
  | "eventAt"
  | "meetings"
  | "coaches"
  | "creditsUsed"
  | "intro"
  | "session"
  | "creditsUsed"
  | "user__name"
  | "status"
  | "completedAt"
  | "lastReminderAt"
  | "isCompleted"
  | "pendingStatus"
  | "actions";

// ================================
// Components
// ================================

type ComponentsQuery = NonNullable<ProgramComponentsQuery["programComponents"]>;

export type ParticipantComponent = NonNullableArrayItem<
  ComponentsQuery["participantsComponents"]
>;

export type LeadComponent = NonNullableArrayItem<
  ComponentsQuery["leadsComponents"]
>;

export type ChildComponent = NonNullableArrayItem<
  ParticipantComponent["childComponents"]
>;

export type MainComponent = NonNullable<
  ProgramComponentQuery["programComponent"]
>;

export type ResponseComponent = NonNullable<
  NonNullable<
    ProgramComponentResponseQuery["programComponentResponse"]
  >["programComponent"]
>;

export type StatisticComponent = NonNullable<
  ProgramComponentForStatisticQuery["programComponentForStatistic"]
>;

type ParentComponent = NonNullable<ParticipantComponent["parentComponent"]>;

export function isLeadComponent(
  component:
    | LeadComponent
    | MainComponent
    | ParticipantComponent
    | ParentComponent,
): component is LeadComponent {
  return "totalParticipants" in component && "isSetupComplete" in component;
}

export function isParticipantComponent(
  component: LeadComponent | MainComponent | ParticipantComponent,
): component is ParticipantComponent {
  return component && "participantSubmitAt" in component;
}

// ================================
// Inputs
// ================================

export type ComponentInput = Omit<ProgramComponentInput, "formFields"> & {
  roleplayScenarios?: ProgramScenario[];
  formFields?: CustomFormField[];
  selectedCoachIds?: string[];
  status?: ComponentStatus;
};

// ================================
// Participants
// ================================

export type CoachParticipant = NonNullableArrayItem<
  GetProgramCoachingSessionParticipantsQuery["programCoachingSessionParticipants"]
>;

export type ComponentParticipant = NonNullableArrayItem<
  NonNullable<
    ProgramComponentParticipantsQuery["programComponentParticipants"]
  >["participants"]
>;

export type ProgramParticipant = NonNullableArrayItem<
  NonNullable<ProgramUsersQuery["programUsers"]>["data"]
>;

// ================================
// Programs
// ================================

export type MainProgram = NonNullable<ProgramQuery["program"]>;
export type MiniProgram = NonNullableArrayItem<
  NonNullable<ProgramsQuery["programs"]>["data"]
>;

type CreateProgramMutationProgram = NonNullable<
  NonNullable<CreateProgramMutation["createProgram"]>["program"]
>;

type CloneProgramMutationProgram = NonNullable<
  NonNullable<CloneProgramMutation["cloneProgram"]>["program"]
>;

export type NewProgram =
  | CreateProgramMutationProgram
  | CloneProgramMutationProgram;

// ================================
// Misc
// ================================

export type CustomFormField = Resolve<
  Omit<ProgramFormField, "id" | "required" | "link"> & {
    id?: string | null;
    link?: string | null;
    prompt?: string | null;
    order: number;
    required?: boolean;
    metadata?: {
      text: string;
      imageSrc?: string | null;
    };
    options?: {
      id?: string | null;
      label: string;
      value: string;
    }[];
  }
>;

// ================================
// Panels
// ================================

export type PanelComponent =
  | LeadComponent
  | ParticipantComponent
  | MainComponent;

export interface PanelProps {
  component: PanelComponent;
  program: MainProgram;
  role: ProgramRole;
  large?: boolean;
  onEdit?: (component?: PanelComponent) => void;
  isParticipantView?: boolean;
  view?: "list" | "feed";
}

// ================================
// Component Tabs
// ================================

export interface NextInputProps {
  value?: Partial<ComponentInput>;
  isSave?: boolean;
  isNext?: boolean;
  skipValidation?: boolean;
  introSessionValue?: ChildProgramComponentInput;
  meetingSessionValue?: ChildProgramComponentInput[];
}

export type ConfigureInput = ComponentInput & {
  formFields: CustomFormField[];
  productId?: string;
};

export interface ComponentTabProps {
  onNext: (data: NextInputProps) => void;
  input: ConfigureInput;
  saving: boolean;
  programId: string;
  targetLaunchDate?: string | null;
  programStatus?: ProgramStatus | null;
  programComponent: MainComponent;
  onClose: () => void;
  onAddMore: () => void;
  isEdit: boolean;
  introSessionInput?: ChildProgramComponentInput;
  meetingSessionInput: ChildProgramComponentInput[];
  surveyTemplateData?: SurveyTemplatesQuery;
}
